import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { CATEGORY_IMAGES } from "../assets/images";
import logoBCT from "../assets/images/logoBC.png";
import Breadcrumbs from "../components/common/Breadcrumbs";
import SEO from "../components/seo/SEO";

import NoData from "../components/common/NoData";
import {
  getCategoryItems,
  getCityData,
  getItemData,
} from "../utils/getCityData";
import slugify from "../utils/slugify";
import PlaceCard from "./PlaceCard";
import ShareActions from "../components/common/ShareActions";

const Item = () => {
  const { city, category, item } = useParams();
  const cityData = getCityData(city);
  const itemData = getItemData(city, category, item);
  const nearbyItems = getCategoryItems(city, category).filter(
    (i) => slugify(i.name) !== slugify(itemData?.name),
  );

  if (!itemData) {
    return (
      <NoData
        title="Place not found"
        description="This place may have been removed or the link is incorrect."
        showContact
      />
    );
  }

  const heroImage =
    itemData.image || CATEGORY_IMAGES[category] || CATEGORY_IMAGES.tourist;

  const isSingleNearby = nearbyItems.length === 1;

  return (
    <>
      <SEO
        title={`${itemData.name} in ${cityData.name} | NearbyGuide`}
        description={`${itemData.name} located in ${itemData.area}, ${cityData.name}. View timings, location, details and nearby places.`}
        canonical={`https://nearbyguide.in/city/${city}/${category}/${slugify(itemData.name)}`}
        image={CATEGORY_IMAGES[category]}
        key={`${itemData.name}, ${itemData.name} ${cityData.name}, ${itemData.name} ${itemData.area}, ${category} in ${cityData.name}, ${category} in ${itemData.area}, best ${category} in ${cityData.name}, nearby ${category} in ${itemData.area}, ${itemData.name} timings, ${itemData.name} location, ${itemData.name} address, ${itemData.name} near me, ${itemData.name} details, ${itemData.name} nearby places, ${cityData.name} ${category} places, NearbyGuide ${itemData.name}`}
      />

      <Container
        FmaxWidth="lg"
        sx={{ py: { xs: 3, md: 5 }, position: "relative", zIndex: 1 }}
      >
        <Breadcrumbs city={city} category={category} item={itemData.name} />
        <ShareActions
          title={`${itemData.name} | NearbyGuide`}
          text={`${itemData.name} located in ${itemData.area}, ${cityData.name}. View details, timings and map.`}
        />

        {/* ===== HERO IMAGE ===== */}
        <Box
          sx={{
            width: "100%",
            height: { xs: 250, md: 360 },
            borderRadius: 2,
            overflow: "hidden",
            mb: 4,
            position: "relative",
            backgroundColor: "#eee",
          }}
        >
          {/* IMAGE */}
          <Box
            component="img"
            src={heroImage}
            alt={itemData.name}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />

          {/* WATERMARK LOGO */}
          <Box
            component="img"
            src={logoBCT}
            alt="NearbyGuide"
            sx={{
              position: "absolute",
              bottom: 14,
              right: 16,
              width: { xs: 90, md: 120 },
              opacity: 0.9,
              pointerEvents: "none",
            }}
          />
        </Box>

        {/* ===== TITLE ===== */}
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "1.9rem", md: "2.6rem" },
            fontWeight: 900,
            lineHeight: 1.15,
            color: "var(--text-heading)",
          }}
        >
          {itemData.name}
        </Typography>

        <Typography
          sx={{
            color: "var(--text-secondary)",
            mt: 0.8,
            mb: 3,
            fontSize: "0.95rem",
          }}
        >
          📍 {itemData.area}, {cityData.name}
        </Typography>

        {/* ===== MAIN CONTENT ===== */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="stretch"
        >
          {/* LEFT CONTENT */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography
                  sx={{
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    color: "var(--text-primary)",
                    mb: 2,
                  }}
                >
                  {itemData.details}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1.2}>
                  {itemData.timing && (
                    <Typography>
                      ⏰ <strong>Timing:</strong> {itemData.timing}
                    </Typography>
                  )}
                  {itemData.ticket && (
                    <Typography>
                      🎟 <strong>Ticket:</strong> {itemData.ticket}
                    </Typography>
                  )}
                  {itemData.price && (
                    <Typography>
                      💰 <strong>Price:</strong> {itemData.price}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* RIGHT ACTION CARD */}
          <Box
            sx={{
              width: { xs: "100%", md: 320 },
              position: { md: "sticky" },
              top: { md: 90 },
              alignSelf: "flex-start",
            }}
          >
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} mb={1}>
                  Quick Actions
                </Typography>

                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: "var(--color-neon)",
                    fontWeight: 600,
                  }}
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    itemData.map,
                  )}`}
                  target="_blank"
                >
                  📍 Open in Google Maps
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Stack>

        {/* ===== NEARBY PLACES ===== */}
        {nearbyItems?.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <Typography
              sx={{
                fontSize: { xs: "1.3rem", md: "1.6rem" },
                fontWeight: 800,
                mb: 2.5,
                color: "var(--text-heading)",
              }}
            >
              Nearby Mandirs in {cityData.name}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2.5,
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              {nearbyItems.slice(0, 6).map((place) => (
                <Box
                  key={place.name}
                  sx={{
                    flex: {
                      xs: "1 1 100%",
                      sm: isSingleNearby
                        ? "0 1 420px" // 👈 single item width
                        : "1 1 calc(50% - 20px)",
                      md: isSingleNearby
                        ? "0 1 420px"
                        : "1 1 calc(33.333% - 20px)",
                    },
                    maxWidth: isSingleNearby ? 420 : "100%",
                  }}
                >
                  <PlaceCard
                    item={place}
                    category={category}
                    link={`/city/${city}/${category}/${slugify(place.name)}`}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Container>
    </>
  );
};

export default Item;
